/* eslint-disable object-curly-newline,implicit-arrow-linebreak */
import { useEffect, useState, useTransition } from 'react'
import { Grid, Link } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'

const styles = (theme, { value }) => ({
  root: {},
  link: {
    display: 'flex',
    alignItems: 'center',
  },
  iconLink: {
    verticalAlign: 'middle',
    ...value && { marginRight: '0.5rem' },
  },
  ...theme.custom.iconLink,
})

/**
 * IconLink UI Element.
 * Displays clickable/link icon with text.
 *
 * @param {Object} forwardedRef             a reference to the root element
 * @param {string} [value]                  a string to be used as value
 * @param {boolean} [hidden]                whether the component should be hidden
 * @param {string} [prefix]                 a string to be used as value
 * @param {Object|string} [icon]            the name or configuration of an icon
 * @param {string} [events]                 the events to be fired by the element
 * @param {boolean} [autoHide]              whether the element should be hidden if `value`
 *                                          is falsy
 * @param {Object} [props]                  additional element properties
 * @param {string} [props.target]           optional link target
 * @param {string} [props.variant]          a text style variant to set for the element
 * @param {string} [props.color]            a color style to set for the element
 * @param {string} [props.disabled]         whether the element is not interactive
 * @param {Object} [props.events]           events for the link
 * @param {string} [props.iconColor]        optional specific color for the icon
 * @param {string} [props.className]        optional style classes for the root element
 * @param {Function} [props.events.onOpen]  onOpen handler
 * @param {Function} [props.events.onClick] onClick handler
 * @returns {JSX.Element}                   the new IconLink element
 */
const IconLink = ({ forwardedRef, value, hidden, prefix, icon, events, autoHide, ...props }) => {
  const classes = useStyles(styles, { value })()
  const { onOpen, onClick } = events || {}
  const [, startTransition] = useTransition()
  const [href, setHref] = useState(() => onOpen?.(null))

  useEffect(() => {
    setHref(onOpen?.(null))
  }, [onOpen])

  const handleClick = event => startTransition(() => onClick?.(event))

  return (autoHide && !value) || hidden ? null : (
    <Grid
      ref={forwardedRef}
      className={props.className || classes.root}
      container
      item
    >
      <Link
        href={href || `${prefix ? `${prefix}:` : 'http://'}${value}`}
        data-rel={'external'}
        target={props.target || '_blank'}
        rel={'noopener'}
        className={classes.link}
        variant={props.variant}
        color={props.color}
        tabIndex={props.disabled && -1}
        onClick={handleClick}
        style={
          props.disabled && {
            pointerEvents: 'none',
            cursor: 'default',
            '&:hover': {
              textDecoration: 'none',
            },
          }
        }
        underline={'hover'}
      >
        <Icon
          source={icon}
          className={classes.iconLink}
          color={props.iconColor || props.color}
        />
        {value}
      </Link>
    </Grid>
  )
}

export default useMemoRef(IconLink, props =>
  [props.value, props.events, props.color, props.variant, props.className])
